<script setup>
import PartneroBranding from "../PartneroBranding.vue";
import {isCustomColor, isRoute} from "../../../helpers/common"
import {computed} from "vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import EditableTextEditorField from "../portal-editable-fields/EditableTextEditorField.vue";
import EditableTextField from "../portal-editable-fields/EditableTextField.vue";
import EditableImageField from "../portal-editable-fields/EditableImageField.vue";
import EditableSymbolField from "../portal-editable-fields/EditableSymbolField.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const getMaxWidthClass = computed(() => {
    const width = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_width
        : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width;

    if (width === 'lg:w-8/12') return 'max-w-3xl';
    if (width === 'lg:w-6/12') return 'max-w-lg';
    return 'max-w-md';
});

const getTextColor = computed(() => {
    const color = isCustomColor(portalContentAndDesignStore.portalLayoutSettings.public_page_text_color) ? 'custom-text' : portalContentAndDesignStore.portalLayoutSettings.public_page_text_color;
    return 'text-' + color;
});

const getBackgroundImage = computed(() => {
    return (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.srcRoutes.portal_signup_file_background
        : portalContentAndDesignStore.srcRoutes.portal_login_file_background;
});

const getSidebarType = computed(() => {
    return (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? (portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_type || 'image')
        : (portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_type || 'image');
});

const getSidebarBranding = computed(() => {
    return (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? (portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_branding || 'none')
        : (portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_branding || 'none');
});

const isSignupDifferentAppearance = computed(() => {
    return portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance;
});

// Universal fallback copy shown on the login/signup sidebar (both the live
// portal and the Live Designer preview) when the portal has a content sidebar
// but the customer hasn't provided their own text. These are display-only
// defaults: the editable fields only emit on user input, so the fallback is
// never persisted unless the customer actually edits it.
const DEFAULT_SIDEBAR_HEADLINE = 'Partner with us and grow together';
const DEFAULT_SIDEBAR_CONTENT = '<p>Join our partner program to earn rewards for every customer you refer. Track your referrals, grab ready-made promotional resources, and manage your payouts — all from one dashboard.</p>';

const useDefaultSidebarText = computed(() => {
    return getSidebarType.value === 'content';
});

const getSidebarHeadline = computed({
    get: () => {
        const value = isSignupDifferentAppearance.value
            ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_headline
            : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_headline;

        if (useDefaultSidebarText.value && (!value || value.trim() === '')) {
            return DEFAULT_SIDEBAR_HEADLINE;
        }

        return value;
    },
    set: (val) => {
        if (isSignupDifferentAppearance.value) {
            portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_headline = val;
        } else {
            portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_headline = val;
        }
    }
});

const getSidebarContent = computed({
    get: () => {
        const value = isSignupDifferentAppearance.value
            ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_content
            : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_content;

        const stripped = value ? value.replace(/<[^>]*>/g, '').trim() : '';
        if (useDefaultSidebarText.value && stripped.length === 0) {
            return DEFAULT_SIDEBAR_CONTENT;
        }

        return value;
    },
    set: (val) => {
        if (isSignupDifferentAppearance.value) {
            portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_content = val;
        } else {
            portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_content = val;
        }
    }
});

const sidebarContentExists = computed(() => {
    const val = getSidebarContent.value;
    if (!val) return false;
    const stripped = val.replace(/<[^>]*>/g, '').trim();
    return stripped.length > 0;
});

const hasSidebarContent = computed(() => {
    return (getSidebarBranding.value && getSidebarBranding.value !== 'none') ||
           (getSidebarHeadline.value && getSidebarHeadline.value.trim() !== '') ||
           sidebarContentExists.value;
});

const getSidebarBgColor = computed(() => {
    const color = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? (portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_bg_color || 'brand')
        : (portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_bg_color || 'brand');

    // If 'brand' is selected, use the primary brand color
    if (color === 'brand') {
        return getPrimaryColor.value;
    }

    return isCustomColor(color) ? 'custom-sidebar-bg' : `bg-${color}`;
});

const getSidebarTextColor = computed(() => {
    const color = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? (portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_text_color || 'white')
        : (portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_text_color || 'white');

    return isCustomColor(color) ? 'custom-sidebar-text' : `text-${color}`;
});

const getSidebarBgStyle = computed(() => {
    const color = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_bg_color
        : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_bg_color;

    // If 'brand' is selected, check if brand color is custom
    if (color === 'brand') {
        const brandColor = portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color;
        if (isCustomColor(brandColor)) {
            return { backgroundColor: brandColor };
        }
        return {};
    }

    if (isCustomColor(color)) {
        return { backgroundColor: color };
    }
    return {};
});

const getSidebarTextStyle = computed(() => {
    const color = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_text_color
        : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_text_color;

    if (isCustomColor(color)) {
        return { color: color };
    }
    return {};
});

const shouldShowSidebar = computed(() => {
    const sidebarSide = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_side
        : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side;

    // Sidebar is disabled
    if (sidebarSide === 'none') {
        return false;
    }

    // Check if sidebar type is content
    if (getSidebarType.value === 'content') {
        // In live designer mode, always show content sidebar so users can preview
        if (portalContentAndDesignStore.liveDesignerEnabled) {
            return true;
        }
        // In regular mode, only show if any content exists (branding, headline, or description)
        return hasSidebarContent.value;
    }

    // For image type, check for valid uploaded image
    const imageValue = getBackgroundImage.value;
    const hasImage = imageValue &&
                     imageValue !== null &&
                     imageValue !== undefined &&
                     imageValue !== '' &&
                     imageValue !== portalContentAndDesignStore.getFileUploadNoImageRoute;

    return hasImage;
});

const layout = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout)
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none'
    ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners
    : '')

const symbol = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol)
const logo = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_logo)
const brandingType = computed(() => {
    if (isRoute('partners.login.show')) {
        return portalContentAndDesignStore.loginPageSettings?.program_login_page_branding_type;
    }
    if (isRoute('partners.register.show')) {
        return portalContentAndDesignStore.signupPageSettings?.signup?.program_signup_page_branding_type || 'none';
    }

    return 'none';
})

const shouldShowHeading = computed(() => {
    return [
        'partners.login.show',
        'partners.register.show',
        'partners.password.request.show',
        'partners.password.reset.show'
    ].includes(route().current());
})

const getHeadingName = computed(() => {
    if (isRoute('partners.login.show')) {
        return 'login_label';
    }

    if (isRoute('partners.register.show')) {
        return 'register_label';
    }

    if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
        return 'password_reset_label';
    }
});

const getPrimaryColor = computed(() => {
    const primaryColor = isCustomColor(portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color)
        ? 'custom-primary'
        : portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color;

    return 'bg-' + primaryColor;
});

const getHeadingStoreField = computed({
    get: () => {
        if (isRoute('partners.login.show')) {
            return portalContentAndDesignStore.translationSettings.portal_login_title
                ? portalContentAndDesignStore.translationSettings.portal_login_title
                : portalContentAndDesignStore.translationSettings.portal_translation_login_login_to;
        }

        if (isRoute('partners.register.show')) {
            // Show approval title if inline approval is active and has content
            if (portalContentAndDesignStore.showInlineApproval) {
                // Use approval title if available, otherwise fallback to signup title
                if (portalContentAndDesignStore.approvalData.title) {
                    return portalContentAndDesignStore.approvalData.title;
                }
            }

            return portalContentAndDesignStore.translationSettings.portal_signup_title
                ? portalContentAndDesignStore.translationSettings.portal_signup_title
                : portalContentAndDesignStore.translationSettings.portal_translation_signup_register_to;
        }

        if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            return portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_reset_password;
        }
    },
    set: (val) => {
        if (isRoute('partners.login.show')) {
            portalContentAndDesignStore.translationSettings.portal_login_title
                ? portalContentAndDesignStore.translationSettings.portal_login_title = val
                : portalContentAndDesignStore.translationSettings.portal_translation_login_login_to = val;
        } else if (isRoute('partners.register.show')) {
            portalContentAndDesignStore.translationSettings.portal_signup_title
                ? portalContentAndDesignStore.translationSettings.portal_signup_title = val
                : portalContentAndDesignStore.translationSettings.portal_translation_signup_register_to = val;
        } else if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_reset_password = val;
        }
    }
});

const getSubheaderStoreField = computed({
    get: () => {
        if (isRoute('partners.login.show')) {
            return portalContentAndDesignStore.translationSettings.portal_login_description;
        }

        if (isRoute('partners.register.show')) {
            // Show approval description if inline approval is active and has content
            if (portalContentAndDesignStore.showInlineApproval) {
                // Use approval description if available, otherwise fallback to signup description
                if (portalContentAndDesignStore.approvalData.description) {
                    return portalContentAndDesignStore.approvalData.description;
                }
            }

            return portalContentAndDesignStore.translationSettings.portal_signup_description;
        }

        // Password reset pages don't use subheader - "Back to login" link is handled in the component
        return null;
    },
    set: (val) => {
        if (isRoute('partners.login.show')) {
            portalContentAndDesignStore.translationSettings.portal_login_description = val;
        } else if (isRoute('partners.register.show')) {
            portalContentAndDesignStore.translationSettings.portal_signup_description = val;
        }
    }
});

const subheaderExists = computed(() => {
    const val = getSubheaderStoreField.value;
    if (!val) return false;
    const stripped = val.replace(/<[^>]*>/g, '').trim();
    return stripped.length > 0;
})

</script>

<template>
    <div
        v-if="portalContentAndDesignStore.portalInitialized"
        class="flex-1 h-screen flex overflow-hidden"
        :class="{ 'flex-row-reverse':
            shouldShowSidebar && (
                (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
                 ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_side === 'right'
                 : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side === 'right'
            )
        }"
        v-cloak
    >
        <div
            class="flex w-full"
            :class="shouldShowSidebar
                ? ((portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
                    ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_width
                    : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width)
                : 'flex-1'"
        >
            <div
                :class="[
                    'flex flex-1 px-6 py-6 lg:py-12 overflow-y-auto',
                    portalContentAndDesignStore.getPageBackgroundColorClass,
                    portalContentAndDesignStore.getPageBackgroundGradientColorClass
                ]"
            >
                <div
                    class="m-auto w-full relative"
                    :class="getMaxWidthClass"
                >
                    <div
                        class="text-center space-y-2"
                        :class="getTextColor"
                    >
                        <slot name="subheader">
                            <div v-if="brandingType && brandingType !=='none'" class="flex justify-center py-3">
                                <template v-if="brandingType === 'symbol' && symbol">
                                    <div
                                        class="relative h-16 w-16 lg:h-20 lg:w-20 flex-shrink-0 bg-cover bg-center rounded-lg"
                                        :class="corners"
                                        :style="{ backgroundImage: `url(${symbol})` }"
                                    ></div>
                                </template>

                                <template v-else-if="brandingType === 'logo' && logo">
                                    <img :src="logo" class="h-10 lg:h-12 w-auto" alt="" />
                                </template>
                            </div>
                            <editable-text-field
                                v-if="shouldShowHeading"
                                :name="getHeadingName"
                                v-model="getHeadingStoreField"
                                text-type="h2"
                            >
                                <template #custom-content>
                                    {{ getHeadingStoreField }}
                                    {{
                                        (portalContentAndDesignStore.translationSettings?.portal_login_title ||
                                         portalContentAndDesignStore.translationSettings?.portal_signup_title ||
                                         (portalContentAndDesignStore.showInlineApproval && portalContentAndDesignStore.approvalData.title))
                                            ? ''
                                            : (portalContentAndDesignStore.program.name ?? '')
                                    }}
                                </template>
                            </editable-text-field>

                            <h2 v-else class="text-3xl font-semibold">
                              {{ portalContentAndDesignStore.program.name }}
                            </h2>

                            <editable-text-editor-field
                                v-if="subheaderExists || portalContentAndDesignStore.liveDesignerEnabled"
                                v-model="getSubheaderStoreField"
                                class="partnero-page-content-section text-opacity-80"
                            />
                        </slot>
                    </div>

                    <div class="mt-10">
                      <slot name="content"></slot>
                    </div>

                    <partnero-branding
                        :portal-general-partnero-branding-enabled="portalContentAndDesignStore.partneroBrandingSettings?.enabled ?? false"
                        :unique-key-link="portalContentAndDesignStore.partneroBrandingSettings?.unique_key_link ?? ''"
                    />
                </div>
            </div>
        </div>
        <div
            v-if="shouldShowSidebar"
            class="flex-1 hidden lg:flex relative"
            :class="getSidebarType === 'image' ? getPrimaryColor : ''"
        >
            <!-- Image sidebar -->
            <img
                v-if="getSidebarType === 'image'"
                class="absolute inset-0 h-full w-full object-cover"
                :src="getBackgroundImage"
                alt=""
            />
            <!-- Content sidebar -->
            <div
                v-else-if="getSidebarType === 'content'"
                class="absolute inset-4 lg:inset-6 overflow-hidden"
                :class="[getSidebarBgColor, corners]"
                :style="getSidebarBgStyle"
            >
                <div
                    class="p-8 lg:p-12 xl:p-16 h-full flex flex-col justify-center overflow-y-auto"
                    :class="getSidebarTextColor"
                    :style="getSidebarTextStyle"
                >
                    <!-- Has content to display -->
                    <div v-if="hasSidebarContent" class="sidebar-editable-content">
                        <!-- Branding (Symbol or Logo) -->
                        <div v-if="getSidebarBranding && getSidebarBranding !== 'none'" class="mb-6">
                            <editable-symbol-field
                                v-if="getSidebarBranding === 'symbol' && symbol"
                                :symbol-wrapper-classes="'relative h-16 w-16 lg:h-20 lg:w-20 flex-shrink-0 bg-cover bg-center rounded-lg'"
                                symbol-name="portal_layout_file_symbol"
                                v-model:symbol-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol"
                                :symbol-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                                :symbol-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                            />
                            <editable-image-field
                                v-else-if="getSidebarBranding === 'logo' && logo"
                                v-model:image-path="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo"
                                img-name="portal_layout_file_logo"
                                title="Logo"
                                :image-upload-url="portalContentAndDesignStore.getFileUploadRoute"
                                :image-delete-url="portalContentAndDesignStore.getFileDeleteRoute"
                                img-class="h-10 lg:h-12 w-auto"
                            />
                        </div>

                        <!-- Headline -->
                        <editable-text-field
                            v-if="(getSidebarHeadline && getSidebarHeadline.trim() !== '') || portalContentAndDesignStore.liveDesignerEnabled"
                            v-model="getSidebarHeadline"
                            text-type="custom"
                            class="text-2xl lg:text-3xl xl:text-4xl font-bold mb-4"
                        />

                        <!-- Description/Content -->
                        <editable-text-editor-field
                            v-if="sidebarContentExists || portalContentAndDesignStore.liveDesignerEnabled"
                            v-model="getSidebarContent"
                            class="prose prose-lg max-w-none sidebar-content"
                        />
                    </div>

                    <!-- Live designer placeholder when no content -->
                    <div
                        v-else-if="portalContentAndDesignStore.liveDesignerEnabled"
                        class="opacity-60 text-center"
                    >
                        <p class="text-lg">Your sidebar content will appear here</p>
                        <p class="text-sm mt-2">Add branding, headline, and description in the Page structure settings</p>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
/* Make prose content inherit text color from parent while preserving heading sizes */
.sidebar-content :deep(*) {
    color: inherit;
}
.sidebar-content :deep(h1) {
    font-size: 2.25em;
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 0.5em;
}
.sidebar-content :deep(h2) {
    font-size: 1.5em;
    font-weight: 700;
    line-height: 1.25;
    margin-top: 1em;
    margin-bottom: 0.5em;
}
.sidebar-content :deep(h3) {
    font-size: 1.25em;
    font-weight: 600;
    line-height: 1.35;
    margin-top: 1em;
    margin-bottom: 0.5em;
}
.sidebar-content :deep(p) {
    margin-bottom: 1em;
}
.sidebar-content :deep(ul) {
    list-style-type: disc;
    padding-left: 1.5em;
    margin-bottom: 1em;
}
.sidebar-content :deep(ol) {
    list-style-type: decimal;
    padding-left: 1.5em;
    margin-bottom: 1em;
}
.sidebar-content :deep(li) {
    margin-bottom: 0.25em;
}
.sidebar-content :deep(a) {
    color: inherit;
    text-decoration: underline;
    opacity: 0.9;
}
.sidebar-content :deep(a:hover) {
    opacity: 1;
}

/* Force dark text in sidebar edit mode inputs and text editor (text color may be white) */
.sidebar-editable-content :deep(input) {
    color: #111827;
    background-color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
}
.sidebar-editable-content :deep(.ProseMirror) {
    color: #111827;
}
.sidebar-editable-content :deep(.tiptap-menu button svg) {
    color: #111827;
}
</style>
